import LegalPageLayout, { Section, SubSection, Paragraph, List } from '../../components/LegalPageLayout'

export default function PrivacyPolicyPage() {
  return (
    <LegalPageLayout title="Privacy Policy" lastUpdated="February 1, 2026">
      {/* Introduction */}
      <Section title="1. Introduction">
        <Paragraph>
          ARTIST 360 INC ("DIZEE," "we," "us," or "our") is committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our music industry management platform and related services.
        </Paragraph>
        <Paragraph>
          If you have any questions or concerns about this policy or our practices with regards to your personal
          information, please contact us at contact@dizee.com.
        </Paragraph>
      </Section>

      {/* Information We Collect */}
      <Section title="2. Information We Collect">
        <SubSection title="2.1 Personal Information">
          <Paragraph>We collect personal information that you voluntarily provide to us when you:</Paragraph>
          <List
            items={[
              'Register for an account',
              'Use our services and features',
              'Contact us for support',
              'Subscribe to our newsletter or marketing communications',
            ]}
          />
          <Paragraph>This information may include:</Paragraph>
          <List
            items={[
              'Name and contact information (email address, phone number)',
              'Account credentials (username, password)',
              'Profile information (artist name, bio, photos)',
              'Professional information (tour dates, venues, contacts)',
              'Payment information (processed securely through third-party providers)',
            ]}
          />
        </SubSection>

        <SubSection title="2.2 Automatically Collected Information">
          <Paragraph>When you use our services, we automatically collect certain information:</Paragraph>
          <List
            items={[
              'Device information (IP address, browser type, operating system)',
              'Usage data (pages visited, time spent, features used)',
              'Location data (if you grant permission)',
              'Cookies and similar tracking technologies',
            ]}
          />
        </SubSection>
      </Section>

      {/* How We Use Your Information */}
      <Section title="3. How We Use Your Information">
        <Paragraph>We use your personal information for the following purposes:</Paragraph>
        <List
          items={[
            'Provide Services: To deliver and maintain our platform and features',
            'Account Management: To create and manage your account',
            'Communication: To send you updates, notifications, and support messages',
            'Analytics: To understand how users interact with our services',
            'Marketing: To send promotional content (with your consent)',
            'Security: To protect against fraud and unauthorized access',
            'Legal Compliance: To comply with applicable laws and regulations',
          ]}
        />
      </Section>

      {/* Your Privacy Rights */}
      <Section title="4. Your Privacy Rights">
        <SubSection title="4.1 GDPR Rights (European Users)">
          <Paragraph>
            If you are a resident of the European Economic Area (EEA), you have the following rights:
          </Paragraph>
          <List
            items={[
              'Right to Access: Request access to your personal data',
              'Right to Rectification: Correct inaccurate or incomplete data',
              'Right to Erasure: Request deletion of your personal data ("right to be forgotten")',
              'Right to Restrict Processing: Limit how we use your data',
              'Right to Data Portability: Receive your data in a portable format',
              'Right to Object: Object to certain types of processing',
              'Right to Withdraw Consent: Withdraw consent at any time',
            ]}
          />
        </SubSection>

        <SubSection title="4.2 CCPA Rights (California Residents)">
          <Paragraph>If you are a California resident, you have the following rights under CCPA:</Paragraph>
          <List
            items={[
              'Right to Know: Request disclosure of data we collect, use, and share',
              'Right to Delete: Request deletion of your personal information',
              'Right to Opt-Out: Opt-out of the sale of personal information',
              'Right to Non-Discrimination: Not be discriminated against for exercising your rights',
            ]}
          />
          <Paragraph>
            To exercise any of these rights, please contact us at contact@dizee.com or use the "Cookie Preferences"
            option in our cookie consent banner.
          </Paragraph>
        </SubSection>
      </Section>

      {/* Data Security */}
      <Section title="5. Data Security">
        <Paragraph>
          We implement appropriate technical and organizational measures to protect your personal information against
          unauthorized access, alteration, disclosure, or destruction. These measures include:
        </Paragraph>
        <List
          items={[
            'Encryption of data in transit and at rest (HTTPS, TLS)',
            'Secure authentication and access controls',
            'Regular security audits and vulnerability assessments',
            'Employee training on data protection',
          ]}
        />
        <Paragraph>
          However, no method of transmission over the Internet is 100% secure. We cannot guarantee absolute security.
        </Paragraph>
      </Section>

      {/* Cookies and Tracking */}
      <Section title="6. Cookies and Tracking Technologies">
        <Paragraph>
          We use cookies and similar tracking technologies to enhance your experience. You can control cookie preferences
          through our Cookie Consent banner or your browser settings.
        </Paragraph>
        <Paragraph>Cookie categories we use:</Paragraph>
        <List
          items={[
            'Essential Cookies: Required for the website to function',
            'Analytics Cookies: Help us understand how you use our site',
            'Marketing Cookies: Used to deliver relevant advertisements',
          ]}
        />
      </Section>

      {/* Data Retention */}
      <Section title="7. Data Retention">
        <Paragraph>
          We retain your personal information only for as long as necessary to fulfill the purposes outlined in this
          Privacy Policy, unless a longer retention period is required or permitted by law. When we no longer need your
          information, we will securely delete or anonymize it.
        </Paragraph>
      </Section>

      {/* Third-Party Services */}
      <Section title="8. Third-Party Services">
        <Paragraph>
          We may share your information with third-party service providers who perform services on our behalf, such as
          payment processing, analytics, and customer support. These providers are contractually obligated to protect your
          information and use it only for the purposes we specify.
        </Paragraph>
      </Section>

      {/* Contact Us */}
      <Section title="9. Contact Us">
        <Paragraph>
          If you have questions about this Privacy Policy or wish to exercise your privacy rights, please contact us:
        </Paragraph>
        <div className="mt-4 ds-surface p-6">
          <p className="text-sm mb-3">
            <strong className="font-semibold">Email:</strong>{' '}
            <a
              href="mailto:contact@dizee.com"
              className="text-blue-600 hover:text-blue-700 dark:hover:text-blue-400 transition-colors"
            >
              contact@dizee.com
            </a>
          </p>
          <p className="text-sm">
            <strong className="font-semibold">Address:</strong> ARTIST 360 INC, Los Angeles, California, USA
          </p>
        </div>
      </Section>
    </LegalPageLayout>
  )
}
